import { useCallback, useId, useState } from "react";
import { Loader2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CatalogueField,
  StockListPaper,
} from "@shared-core/data/catalogue-types";
import { useStockListSearch } from "@shared-core/domain/useStockListSearch";
import { cn } from "../../primitives/utils";

/**
 * A paper field's "Other stock, quote on request" as staff type it (manual
 * RFQ slice 4): the merchants' papers matching what is typed, each with its
 * merchant and how many Tharstern codes it folds. Choosing one writes its name
 * as the typed paper; anything else typed stays as written ("Can't find it?
 * Describe it"). The line is quoted by hand either way, unless the paper is
 * one of the field's own options (manual RFQ slice 5: Novatech Matt 170gsm is
 * Silk 170gsm): choosing it selects that option, which prices now.
 */
export function StockListInput({
  field,
  stockListUrl,
  value,
  onChange,
  onPickListed,
}: {
  field: CatalogueField;
  stockListUrl: string;
  value: string;
  onChange: (raw: string) => void;
  /** Selects one of the field's own options in place of the typed paper. */
  onPickListed?: (code: string) => void;
}) {
  const adapter = useDataAdapter();
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const listboxId = useId();
  const search = useCallback(
    (query: string) => adapter.searchStockList(stockListUrl, query),
    [adapter, stockListUrl],
  );
  const { papers, isLoading, isFailed } = useStockListSearch<StockListPaper>(
    value,
    search,
    isOpen,
  );
  // A catalogue item may narrow the dictionary's options: only one the field still offers is picked.
  const offered = new Set((field.options ?? []).map((option) => option.code));
  const listedOf = (paper: StockListPaper) =>
    onPickListed &&
    paper.listedOptionCode &&
    offered.has(paper.listedOptionCode)
      ? paper.listedOptionCode
      : null;
  const choose = (paper: StockListPaper) => {
    const listed = listedOf(paper);
    if (listed) onPickListed?.(listed);
    else onChange(paper.name);
    setIsOpen(false);
    setHighlight(-1);
  };
  const isListOpen = isOpen && papers.length > 0;

  return (
    <div className="relative w-full max-w-md">
      <div className="flex items-center gap-2">
        <input
          autoFocus
          role="combobox"
          aria-expanded={isListOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            highlight >= 0 ? `${listboxId}-${highlight}` : undefined
          }
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setIsOpen(true);
            setHighlight(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          onKeyDown={(e) => {
            if (!isListOpen) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, papers.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, -1));
            } else if (e.key === "Enter" && highlight >= 0) {
              e.preventDefault();
              choose(papers[highlight]);
            } else if (e.key === "Escape") {
              setIsOpen(false);
            }
          }}
          aria-label={field.customLabel ?? `${field.label}, other`}
          maxLength={field.customConstraints?.max_length}
          placeholder="Search the merchants' papers, or describe one"
          className="px-3 py-1.5 rounded-md border border-border bg-card text-sm w-full"
        />
        {isLoading && (
          <Loader2
            className="size-4 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </div>
      {isListOpen && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Papers on the stock list"
          // Keeps focus in the input while a row is clicked, so blur closes only on a real outside click.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-card shadow-md"
        >
          {papers.map((paper, index) => (
            <li
              key={paper.id}
              id={`${listboxId}-${index}`}
              role="option"
              aria-selected={index === highlight}
              onClick={() => choose(paper)}
              className={cn(
                "flex cursor-pointer items-baseline justify-between gap-3 px-3 py-1.5 text-sm",
                index === highlight ? "bg-muted" : "hover:bg-muted",
              )}
            >
              <span>
                {paper.name}
                {listedOf(paper) && (
                  <span className="block text-xs text-emerald-700 dark:text-emerald-400">
                    Our {paper.listedOptionLabel}, priced now
                  </span>
                )}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {paper.merchants.join(", ")} · {paper.codes.length}{" "}
                {paper.codes.length === 1 ? "code" : "codes"}
              </span>
            </li>
          ))}
        </ul>
      )}
      {isFailed && (
        <p className="mt-1 text-xs text-muted-foreground">
          The stock list isn&apos;t answering. Describe the paper instead.
        </p>
      )}
    </div>
  );
}
